/* eslint-disable camelcase */
import axios from 'axios';
import { constants } from '@utils';
import { find } from 'lodash';

/**
 *
 * @param STRIPE_KEY
 * @param image
 * @param purpose
 * @returns {Promise}
 */
const createImageToken = async (STRIPE_KEY, image, purpose = 'identity_document') => {
	const imageData = new FormData();
	imageData.set('file', image);
	imageData.set('purpose', purpose);

	try {
		const token = await axios.post('https://files.stripe.com/v1/files', imageData, {
			headers : {
				Authorization  : `Bearer ${STRIPE_KEY}`,
				'Content-Type' : 'multipart/form-data',
			},
		});
		return token.data;
	} catch (error) {
		return { ...error.response.data };
	}
};

/**
 *
 * @param STRIPE_KEY
 * @param formData {Object} Must contain the fields: accountType, state, city, address, zip, phoneNumber
 * @param imageToken {String} Image stripe token
 * @returns {Promise}
 */
const createAccountToken = async (STRIPE_KEY, formData, imageToken = null) => {
	// eslint-disable-next-line jsx-control-statements/jsx-jcs-no-undef
	const stripe = Stripe(STRIPE_KEY);

	// accountType must be an integer value: individual (1) or company (2)
	// ssn, firstName, lastName, dob, email and imageToken are needed only for individual accounts,
	// taxId and name are needed only for company accounts
	// state must the be name of the state not the id
	// dob must be formatted 'MM/DD/YYYY'
	const {
		accountType, city, state, zip, dob, address, ssn, phoneNumber, firstName, lastName, email, taxId, name,
	} = formData;

	const addressData = {
		country     : 'US',
		line1       : address,
		postal_code : zip,
		state,
		city,
	};

	const dobArray = dob ? dob.split('/') : [];

	const parsePhoneNumber = (rawPhoneNumber) => {
		const cleanPhone = rawPhoneNumber.replace(/\D/g, '');
		const phoneMatch = cleanPhone.match(/^(\d{3})(\d{3})(\d{4})$/);
		return `${phoneMatch[1]}-${phoneMatch[2]}-${phoneMatch[3]}`;
	};

	const accountData = {
		business_type          : find(constants.BANK_ACCOUNT_TYPES, (item) => item.value === accountType).label.toLowerCase(),
		tos_shown_and_accepted : true,
	};

	if (accountType === constants.BANK_ACCOUNT_TYPES.INDIVIDUAL.value) {
		accountData.individual = {
			address    : addressData,
			first_name : firstName,
			last_name  : lastName,
			id_number  : ssn,
			dob        : {
				day   : dobArray[1],
				month : dobArray[0],
				year  : dobArray[2],
			},
			verification : {
				document : {
					front : imageToken,
				},
			},
			phone : phoneNumber ? parsePhoneNumber(phoneNumber) : undefined,
			email,
		};
	} else if (accountType === constants.BANK_ACCOUNT_TYPES.COMPANY.value) {
		accountData.company = {
			address : addressData,
			tax_id  : taxId,
			phone   : phoneNumber ? parsePhoneNumber(phoneNumber) : undefined,
			name,
		};
	}

	try {
		const token = await stripe.createToken('account', accountData);
		if (token.error) return { error : token.error };

		return token.token;
	} catch (error) {
		return { error };
	}
};

/**
 *
 * @param STRIPE_KEY
 * @param formData {object} Must contain the fields: accountType, routingNo, accountNo, bankHolderName
 * @returns {Promise}
 */
const createBankAccountToken = async (STRIPE_KEY, formData) => {
	// eslint-disable-next-line jsx-control-statements/jsx-jcs-no-undef
	const stripe = Stripe(STRIPE_KEY);

	const bankAccountType = find(constants.BANK_ACCOUNT_TYPES, (item) => item.value === formData.accountType).label.toLowerCase();
	const bankAccountData = {
		country             : 'US',
		currency            : 'USD',
		routing_number      : formData.routingNo,
		account_number      : formData.accountNo,
		account_holder_name : formData.bankHolderName,
		account_holder_type : bankAccountType,
	};

	try {
		const token = await stripe.createToken('bank_account', bankAccountData);
		if (token.error) return { error : token.error };

		return token.token;
	} catch (error) {
		return { error };
	}
};

/**
 * BROKEN in Stripe V3 (No longer used)
 * @param STRIPE_KEY
 * @param formData {object} Must contain the fields: cardHolderName, cardNumber, cvc, expiryDate, address, city, state, zip
 * @param callback
 */
const createCreditCardToken = (STRIPE_KEY, formData, callback) => {
	// eslint-disable-next-line jsx-control-statements/jsx-jcs-no-undef
	Stripe.setPublishableKey(STRIPE_KEY);

	const settings = {
		name            : formData.cardHolderName,
		number          : formData.cardNumber.replace(/\s+/g, ''),
		cvc             : formData.cvc,
		exp_month       : formData.expiryDate.substring(0, 2),
		exp_year        : formData.expiryDate.substring(3, 5),
		address_line1   : formData.address,
		address_city    : formData.city,
		address_state   : formData.state,
		address_zip     : formData.zip,
		address_country : 'US',
	};

	// eslint-disable-next-line jsx-control-statements/jsx-jcs-no-undef
	Stripe.card.createToken(settings, (status, creditCardToken) => {
		callback(creditCardToken);
	});
};

export default {
	createImageToken,
	createAccountToken,
	createBankAccountToken,
	createCreditCardToken,
};
